<template>
  <div
    v-if="hasValidData"
    class="chart-range-bar d-flex"
  />
</template>

<script>
import * as d3 from 'd3';

const validateValues = (values) => {
  const requiredProps = ['value', 'minValue', 'maxValue', 'averageMin', 'averageMax'];
  const hasRequiredProps = requiredProps.every((prop) => Object.keys(values).includes(prop));

  if (!hasRequiredProps) {
    console.error('Required props are missing');

    return false;
  }

  Object.keys(values).forEach((key) => {
    if (values[key] === null || values[key] === undefined || typeof values[key] !== 'number') {
      console.error('Invalid prop types');

      return false;
    }
  });

  return !(
    values.minValue < 0 ||
    values.minValue > values.maxValue ||
    values.averageMin < values.minValue ||
    values.averageMin > values.maxValue ||
    values.value < values.minValue ||
    values.value > values.maxValue
  );
};

export default {
  props: {
    values: {
      type: Object,
      required: true,
      validator: validateValues,
    },
    height: {
      type: Number,
      default: 8,
    },
    cornerRadius: {
      type: Number,
      default: 4,
    },
    averageSectionColor: {
      type: String,
      default: '#2aa7ff',
    },
    layoutColor: {
      type: String,
      default: '#ececec',
    },
    pointerColor: {
      type: String,
      default: '#18D06B',
    },
    pointerWidth: {
      type: Number,
      default: 2,
    },
    // By default, the pointer has a height greater than the bar
    // and overlaps it on the top and bottom.
    pointerHeight: {
      type: Number,
      default: 12,
    },
  },
  computed: {
    hasValidData() {
      return validateValues(this.values);
    },
  },
  mounted() {
    if (this.hasValidData) {
      this.buildBar();
    }
  },
  methods: {
    buildBar() {
      const svg = d3.select(this.$el).append('svg').attr('width', '100%').attr('height', this.pointerHeight);

      this.drawLayout(svg);
      this.drawAverage(svg);
      this.drawPointer(svg);
    },
    drawLayout(svg) {
      svg
        .append('rect')
        .attr('x', 0)
        .attr('y', (this.pointerHeight - this.height) / 2)
        .attr('width', '100%')
        .attr('height', this.height)
        .attr('fill', this.layoutColor)
        .attr('rx', this.cornerRadius);
    },
    // Average 'industry' percent in the field/sector this chart/bar belongs to.
    drawAverage(svg) {
      const { startPercent, lengthPercent } = this.getAverageRangePercentage();

      svg
        .append('rect')
        .attr('x', `${startPercent}%`)
        .attr('y', (this.pointerHeight - this.height) / 2)
        .attr('width', `${lengthPercent}%`)
        .attr('height', this.height)
        .attr('fill', this.averageSectionColor);
    },
    drawPointer(svg) {
      const pointerPercentage = this.getPointerPositionPercentage();

      svg
        .append('line')
        .style('stroke', this.pointerColor)
        .style('stroke-width', this.pointerWidth)
        .attr('x1', `${pointerPercentage}%`)
        .attr('y1', 0)
        .attr('x2', `${pointerPercentage}%`)
        .attr('y2', this.pointerHeight);
    },
    getPointerPositionPercentage() {
      const { value, minValue, maxValue } = this.values;
      let factor;

      // We use computed factor to keep a small distance between the both bar sides
      // and the pointer when the value is equal to 0 or 100%
      // 3% is an optimal factor for following the design
      if (value === minValue) {
        factor = 103;
      } else if (value === maxValue) {
        factor = 97;
      } else {
        factor = 100;
      }

      return value === 0 ? 3 : (value / (maxValue - minValue)) * factor;
    },
    getAverageRangePercentage() {
      const { minValue, maxValue, averageMin, averageMax } = this.values;
      const startPercent = (averageMin / (maxValue - minValue)) * 100;
      const endPercent = (averageMax / (maxValue - minValue)) * 100;
      const lengthPercent = endPercent - startPercent;

      return {
        startPercent,
        endPercent,
        lengthPercent,
      };
    },
  },
};
</script>
